import React, { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment-timezone';
import ReactApexChart from 'react-apexcharts';
import { FUEL_EVENT_FILTER } from './fuelEventConstants';
import {
  estimateFuelEventCostRM,
  formatEstimatedEventCostRM,
} from './fuelCostUtils';
import './FuelLevelSignalPanel.css';

const EVENTS_PER_PAGE = 10;
const CHART_ID = 'fuel-level-signal-chart';

const formatChartAxisTime = (value, timezone) => (
  moment(value).tz(timezone).format('MMM DD, h:mm A')
);

const formatChartTooltipTime = (value, timezone) => (
  moment(value).tz(timezone).format('MMM DD, YYYY h:mm A')
);

const EVENT_FILTER_OPTIONS = [
  { key: FUEL_EVENT_FILTER.REFILL, label: 'REFILL', countKey: 'refillsConfirmed' },
  { key: FUEL_EVENT_FILTER.DRAIN, label: 'DRAIN', countKey: 'drainsConfirmed' },
  { key: FUEL_EVENT_FILTER.ALERT, label: 'ALERT', countKey: 'alerts' },
];

const formatLitres = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '—';
  return `${numeric.toFixed(1)} L`;
};

const formatDelta = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '—';
  const prefix = numeric > 0 ? '+' : '';
  return `${prefix}${numeric.toFixed(1)}`;
};

const formatLocation = (latitude, longitude) => {
  const lat = Number(latitude);
  const lng = Number(longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
};

const FuelLevelSignalPanel = ({
  signalView,
  loading = false,
  error = '',
  emptyReason = '',
  assetName = '',
  fuelType = null,
  fuelPriceRows = [],
  fuelPriceByCode = {},
  timezone = 'Asia/Kuala_Lumpur',
  filterRangeStart = null,
  filterRangeEnd = null,
}) => {
  const [activeEventFilters, setActiveEventFilters] = useState(
    () => [FUEL_EVENT_FILTER.REFILL, FUEL_EVENT_FILTER.DRAIN, FUEL_EVENT_FILTER.ALERT],
  );
  const [eventSearch, setEventSearch] = useState('');
  const [eventPage, setEventPage] = useState(1);
  const [zoomRange, setZoomRange] = useState({ min: null, max: null });
  const chartRef = useRef(null);

  useEffect(() => {
    setZoomRange({ min: null, max: null });
  }, [signalView, filterRangeStart, filterRangeEnd]);

  useEffect(() => {
    if (!signalView || filterRangeStart == null || filterRangeEnd == null) return undefined;
    const timer = window.setTimeout(() => {
      chartRef.current?.chart?.zoomX(filterRangeStart, filterRangeEnd);
    }, 0);
    return () => window.clearTimeout(timer);
  }, [signalView, filterRangeStart, filterRangeEnd]);

  const chartXaxisMin = zoomRange.min ?? filterRangeStart;
  const chartXaxisMax = zoomRange.max ?? filterRangeEnd;

  const handleResetZoom = () => {
    setZoomRange({ min: null, max: null });
    const chart = chartRef.current?.chart;
    if (chart && filterRangeStart != null && filterRangeEnd != null) {
      chart.zoomX(filterRangeStart, filterRangeEnd);
      return;
    }
    chart?.resetSeries();
  };

  const chartOptions = useMemo(() => {
    const refillPoints = signalView?.refillMarkers || [];
    const drainPoints = signalView?.drainMarkers || [];

    return {
      chart: {
        id: CHART_ID,
        type: 'line',
        toolbar: { show: false },
        fontFamily: 'Inter',
        animations: { enabled: false },
        zoom: {
          enabled: true,
          type: 'x',
          autoScaleYaxis: true,
        },
        selection: {
          enabled: true,
          type: 'x',
          fill: {
            color: '#14B8A6',
            opacity: 0.12,
          },
          stroke: {
            width: 1,
            color: '#14B8A6',
            opacity: 0.5,
            dashArray: 4,
          },
        },
        events: {
          zoomed(_chartContext, { xaxis }) {
            if (xaxis?.min != null && xaxis?.max != null) {
              setZoomRange({ min: xaxis.min, max: xaxis.max });
            }
          },
          beforeResetZoom() {
            setZoomRange({ min: null, max: null });
          },
        },
      },
      stroke: {
        curve: 'straight',
        width: 2,
      },
      colors: ['#14B8A6'],
      xaxis: {
        type: 'datetime',
        min: chartXaxisMin ?? undefined,
        max: chartXaxisMax ?? undefined,
        labels: {
          formatter: (value) => formatChartAxisTime(value, timezone),
        },
      },
      yaxis: {
        title: { text: 'Litres' },
        min: 0,
        labels: {
          formatter: (value) => Number(value).toFixed(0),
        },
      },
      legend: {
        show: true,
        position: 'top',
      },
      tooltip: {
        x: {
          formatter: (value) => formatChartTooltipTime(value, timezone),
        },
        y: {
          formatter: (value) => formatLitres(value),
        },
      },
      annotations: {
        points: [
          ...refillPoints.map((point) => ({
            x: point.x,
            y: point.y,
            marker: {
              size: 6,
              fillColor: '#14B8A6',
              strokeColor: '#FFFFFF',
              strokeWidth: 2,
            },
            label: {
              borderColor: '#14B8A6',
              style: {
                color: '#FFFFFF',
                background: '#14B8A6',
                fontSize: '10px',
              },
              text: '↑',
            },
          })),
          ...drainPoints.map((point) => ({
            x: point.x,
            y: point.y,
            marker: {
              size: 6,
              fillColor: '#F04438',
              strokeColor: '#FFFFFF',
              strokeWidth: 2,
            },
            label: {
              borderColor: '#F04438',
              style: {
                color: '#FFFFFF',
                background: '#F04438',
                fontSize: '10px',
              },
              text: '↓',
            },
          })),
        ],
      },
    };
  }, [signalView, timezone, chartXaxisMin, chartXaxisMax]);

  const chartSeries = useMemo(() => ([
    { name: 'confirmed (debounced)', data: signalView?.confirmedSeries || [] },
  ]), [signalView]);

  const motionSegments = signalView?.motionSegments || [];
  const visibleRangeStart = chartXaxisMin ?? signalView?.rangeStart;
  const visibleRangeEnd = chartXaxisMax ?? signalView?.rangeEnd;
  const visibleRangeSpan = visibleRangeStart != null && visibleRangeEnd != null
    ? Math.max(visibleRangeEnd - visibleRangeStart, 1)
    : 1;

  const visibleMotionSegments = useMemo(() => {
    if (visibleRangeStart == null || visibleRangeEnd == null) return motionSegments;
    return motionSegments
      .map((segment) => {
        const segStart = Math.max(segment.start, visibleRangeStart);
        const segEnd = Math.min(segment.end, visibleRangeEnd);
        if (segEnd <= segStart) return null;
        return { ...segment, start: segStart, end: segEnd };
      })
      .filter(Boolean);
  }, [motionSegments, visibleRangeStart, visibleRangeEnd]);

  const isZoomed = zoomRange.min != null && zoomRange.max != null;

  const signalChain = signalView?.signalChain || {};
  const events = (signalView?.events || []).filter(
    (event) => event.eventFilterKey !== FUEL_EVENT_FILTER.SPIKE_REJECTED,
  );

  const eventCounts = useMemo(() => {
    const counts = {
      refillsConfirmed: signalChain.refillsConfirmed || 0,
      drainsConfirmed: signalChain.drainsConfirmed || 0,
      alerts: 0,
    };
    events.forEach((event) => {
      if (event.eventFilterKey === FUEL_EVENT_FILTER.ALERT) {
        counts.alerts += 1;
      }
    });
    return counts;
  }, [events, signalChain]);

  const filteredEvents = useMemo(() => {
    const search = eventSearch.trim().toLowerCase();
    return events.filter((event) => {
      if (!activeEventFilters.includes(event.eventFilterKey)) return false;
      if (!search) return true;
      const location = formatLocation(event.latitude, event.longitude) || '';
      const haystack = [
        event.event,
        event.eventSub,
        event.engineOn ? 'running siphon' : '',
        String(event.deltaLitres),
        String(event.levelAfter),
        String(event.fuelLevelLitre),
        String(estimateFuelEventCostRM(event, fuelType, fuelPriceRows, fuelPriceByCode)),
        location,
      ].join(' ').toLowerCase();
      return haystack.includes(search);
    });
  }, [events, activeEventFilters, eventSearch, fuelType, fuelPriceRows, fuelPriceByCode]);

  const totalEventPages = Math.max(1, Math.ceil(filteredEvents.length / EVENTS_PER_PAGE));
  const paginatedEvents = filteredEvents.slice(
    (eventPage - 1) * EVENTS_PER_PAGE,
    eventPage * EVENTS_PER_PAGE,
  );

  const toggleEventFilter = (filterKey) => {
    setEventPage(1);
    setActiveEventFilters((prev) => {
      if (prev.includes(filterKey)) {
        if (prev.length === 1) return prev;
        return prev.filter((key) => key !== filterKey);
      }
      return [...prev, filterKey];
    });
  };

  if (loading) {
    return (
      <div className="fuel-level-signal-panel">
        <div className="fuel-level-signal-loading">Loading fuel level signal…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fuel-level-signal-panel">
        <div className="fuel-level-signal-error" role="alert">{error}</div>
      </div>
    );
  }

  if (!signalView || (signalView.confirmedSeries?.length || 0) === 0) {
    return (
      <div className="fuel-level-signal-panel">
        <div className="fuel-level-signal-empty">
          {emptyReason || 'No fuel level samples for this asset and date range.'}
        </div>
      </div>
    );
  }

  return (
    <div className="fuel-level-signal-panel">
      <div className="fuel-level-signal-header">
        <h3 className="fuel-level-signal-title">
          Fuel Level Signal
          {assetName ? ` — ${assetName}` : ''}
        </h3>
      </div>

      <p className="fuel-level-signal-chain">
        Signal chain:
        {' '}
        {signalChain.hasDetailedCounts ? (
          <>
            {signalChain.rawInputSamples?.toLocaleString() || 0}
            {' '}
            raw input samples →
            {' '}
            {signalChain.fuelBearingSamples?.toLocaleString() || 0}
            {' '}
            fuel-bearing → spike filter rejected
            {' '}
            {signalChain.spikeRejected || 0}
            {' '}
            →
            {' '}
            {signalChain.cleanSamples?.toLocaleString() || 0}
            {' '}
            clean fuel samples
          </>
        ) : (
          <>
            {signalChain.inputSamples?.toLocaleString() || 0}
            {' '}
            input samples → spike filter rejected
            {' '}
            {signalChain.spikeRejected || 0}
          </>
        )}
        {' '}
        → slosh debounce applied →
        {' '}
        {signalChain.refillsConfirmed || 0}
        {' '}
        refills and
        {' '}
        {signalChain.drainsConfirmed || 0}
        {' '}
        drains confirmed.
      </p>

      <div className="fuel-level-signal-chart-wrap">
        <div className="fuel-level-signal-chart-toolbar">
          <span className="fuel-level-signal-chart-hint">Drag on the chart to zoom by time</span>
          {isZoomed ? (
            <div className="fuel-level-signal-zoom-controls">
              <span className="fuel-level-signal-zoom-range">
                Showing
                {' '}
                {formatChartAxisTime(zoomRange.min, timezone)}
                {' '}
                –
                {' '}
                {formatChartAxisTime(zoomRange.max, timezone)}
              </span>
              <button
                type="button"
                className="fuel-level-signal-reset-zoom"
                onClick={handleResetZoom}
              >
                Reset zoom
              </button>
            </div>
          ) : filterRangeStart != null && filterRangeEnd != null ? (
            <span className="fuel-level-signal-zoom-range">
              Date range:
              {' '}
              {formatChartAxisTime(filterRangeStart, timezone)}
              {' '}
              –
              {' '}
              {formatChartAxisTime(filterRangeEnd, timezone)}
            </span>
          ) : null}
        </div>

        <ReactApexChart
          ref={chartRef}
          options={chartOptions}
          series={chartSeries}
          type="line"
          height={320}
        />

        <div className="fuel-level-signal-motion-bar" aria-hidden="true">
          {visibleMotionSegments.map((segment) => {
            const leftPct = ((segment.start - visibleRangeStart) / visibleRangeSpan) * 100;
            const widthPct = Math.max(((segment.end - segment.start) / visibleRangeSpan) * 100, 0.5);
            return (
              <span
                key={`${segment.start}-${segment.end}-${segment.motionInference}`}
                className={`fuel-level-signal-motion-segment fuel-level-signal-motion-segment--${segment.bandClass}`}
                style={{ left: `${leftPct}%`, width: `${widthPct}%` }}
                title={segment.label}
              />
            );
          })}
        </div>

        <div className="fuel-level-signal-motion-legend">
          <span><i className="fuel-level-signal-motion-dot fuel-level-signal-motion-dot--moving" /> Moving</span>
          <span><i className="fuel-level-signal-motion-dot fuel-level-signal-motion-dot--idle" /> Idle</span>
          <span><i className="fuel-level-signal-motion-dot fuel-level-signal-motion-dot--stopped" /> Stopped</span>
          <span><i className="fuel-level-signal-motion-dot fuel-level-signal-motion-dot--refill" /> Refill</span>
          <span><i className="fuel-level-signal-motion-dot fuel-level-signal-motion-dot--drain" /> Drain</span>
        </div>
      </div>

      {events.length > 0 && (
        <div className="fuel-level-signal-events">
          <div className="fuel-level-signal-events-toolbar">
            <div className="fuel-level-signal-event-filters" role="group" aria-label="Event filters">
              {EVENT_FILTER_OPTIONS.map((option) => {
                const count = eventCounts[option.countKey] || 0;
                if (option.key === FUEL_EVENT_FILTER.ALERT && count === 0) return null;
                const isActive = activeEventFilters.includes(option.key);
                return (
                  <button
                    key={option.key}
                    type="button"
                    className={`fuel-level-signal-event-filter${isActive ? ' fuel-level-signal-event-filter--active' : ''}`}
                    aria-pressed={isActive}
                    onClick={() => toggleEventFilter(option.key)}
                  >
                    <span className="fuel-level-signal-event-filter-check" aria-hidden="true">
                      {isActive ? '✓' : ''}
                    </span>
                    {option.label}
                    {' '}
                    {count}
                  </button>
                );
              })}
            </div>

            <div className="fuel-level-signal-events-toolbar-right">
              <input
                type="search"
                className="fuel-level-signal-event-search"
                placeholder="Search events…"
                value={eventSearch}
                onChange={(e) => {
                  setEventSearch(e.target.value);
                  setEventPage(1);
                }}
              />
              <span className="fuel-level-signal-event-count">
                {filteredEvents.length}
                {' '}
                of
                {' '}
                {events.length}
                {' '}
                events
              </span>
            </div>
          </div>

          <table className="fuel-level-signal-events-table">
            <thead>
              <tr>
                <th>Time</th>
                <th>Event</th>
                <th>Δ Litres</th>
                <th>Level after</th>
                <th>Est. RM</th>
                <th>Location</th>
              </tr>
            </thead>
            <tbody>
              {paginatedEvents.length === 0 ? (
                <tr>
                  <td colSpan={6} className="fuel-level-signal-events-empty-row">
                    No events match the selected filters.
                  </td>
                </tr>
              ) : paginatedEvents.map((event) => {
                const isMovingRise = event.eventSub === 'MOVING_RISE';
                const isRunningDrain = event.eventFilterKey === FUEL_EVENT_FILTER.DRAIN
                  && event.engineOn === true;
                const location = formatLocation(event.latitude, event.longitude);
                const mapsUrl = location
                  ? `https://www.google.com/maps?q=${event.latitude},${event.longitude}`
                  : null;
                const estimatedCostRM = estimateFuelEventCostRM(
                  event,
                  fuelType,
                  fuelPriceRows,
                  fuelPriceByCode,
                );
                const estRmClassName = estimatedCostRM == null
                  ? ''
                  : estimatedCostRM < 0
                    ? 'fuel-level-signal-est-rm fuel-level-signal-est-rm--negative'
                    : estimatedCostRM > 0
                      ? 'fuel-level-signal-est-rm fuel-level-signal-est-rm--positive'
                      : 'fuel-level-signal-est-rm';
                return (
                  <tr key={`${event.x}-${event.event}-${event.deltaLitres}-${event.time}`}>
                    <td>{moment(event.timeStart || event.time).tz(timezone).format('MMM DD, YYYY HH:mm:ss')}</td>
                    <td>
                      <span className={`fuel-level-signal-event-badge fuel-level-signal-event-badge--${event.eventFilterKey?.toLowerCase()}`}>
                        {isMovingRise ? '⚑ MOVING RISE' : event.event}
                      </span>
                      {isRunningDrain && (
                        <span className="fuel-level-signal-event-detail">running siphon</span>
                      )}
                    </td>
                    <td>
                      {formatDelta(event.deltaLitres)}
                      {isMovingRise && (
                        <span className="fuel-level-signal-event-explanation">
                          {' '}
                          L rise with vehicle in motion throughout — not counted as refuel
                          (possible sender fault)
                        </span>
                      )}
                    </td>
                    <td>{formatLitres(event.levelAfter)}</td>
                    <td className={estRmClassName}>
                      {formatEstimatedEventCostRM(estimatedCostRM)}
                    </td>
                    <td>
                      {mapsUrl ? (
                        <a
                          href={mapsUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="fuel-level-signal-location-link"
                        >
                          {location}
                        </a>
                      ) : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {filteredEvents.length > EVENTS_PER_PAGE && (
            <div className="fuel-level-signal-events-pagination">
              <span>
                {(eventPage - 1) * EVENTS_PER_PAGE + 1}
                –
                {Math.min(eventPage * EVENTS_PER_PAGE, filteredEvents.length)}
                {' '}
                of
                {' '}
                {filteredEvents.length}
              </span>
              <div className="fuel-level-signal-events-pagination-actions">
                <button
                  type="button"
                  disabled={eventPage <= 1}
                  onClick={() => setEventPage((prev) => Math.max(prev - 1, 1))}
                >
                  prev
                </button>
                <button
                  type="button"
                  disabled={eventPage >= totalEventPages}
                  onClick={() => setEventPage((prev) => Math.min(prev + 1, totalEventPages))}
                >
                  next
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FuelLevelSignalPanel;
